/*    
<summary>
  This functional component "AddUpdateCustomerSetting" defines the props like initial values and functions for the child form component.
  Developer:Aashish Singh, Created Date:04-June-2024
</summary>
<returns>Returns JSX</returns>
*/

import { observer } from "mobx-react";
import React, { useCallback, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import toast from "react-hot-toast";
import { FormattedMessage } from "react-intl";
import { initialPageLimit } from "../../../../constants/pageLimit.constant";
import { useStore } from "../../../../contexts/store-provider";
import IPageInfo from "../../../../models/ICommon";
import { ISorting } from "../../../../models/ISorting";
import DeletePopup from "../../../../shared-components/popup/delete/delete-popup";
import DeletePopupHOC from "../../../../shared-components/popup/delete/delete-popup-hoc";
import BusinessTypeListView from "./components/business-type-list-view";
import AddBusinessTypeSettingsForm from "./forms/add-business-type-form";
import { format } from "path";
import { formatMessage } from "../../../../translations/format-message";

interface IProps {
  id: number;
  modalClosed: () => void;
  showDeletePopup: boolean;
  deletePopupToggleHandler: () => void;
}

const BusinessTypeSettings = (props: IProps) => {
  const [isDetailsVisible, setIsDetailsVisible] = useState<boolean>(false);
  const { masterManagementStore } = useStore();
  const {
    addUpdateBusinessTypeSettingState,
    GetAllBusinessTypeListService,
    getAllBusinessTypeList,
    AddBusinessTypeService,
    UpdateBusinessTypeService,
    DeleteBusinessTypeService,
    GetBusinessTypeByIdService,
    deleteBusinessTypeSettingState,
    getByIdBusinessTypeSettingState,
    getBusinessTypeDetail,
    resetGetAllBusinessTypeSettingState,
    resetGetByIdBusinessTypeSettingState,
    resetdDeleteBusinessTypeSettingState,
    resetAddUpdateBusinessTypeSettingState,
  } = masterManagementStore;
  // const initialValues: IAddCustomerRate = addCustomerRateState;
  const { id, modalClosed, showDeletePopup, deletePopupToggleHandler } = props;

  const onModalClosed = useCallback(() => {
    modalClosed();
    resetAddUpdateBusinessTypeSettingState();
    resetdDeleteBusinessTypeSettingState();
    resetGetAllBusinessTypeSettingState();
    resetGetByIdBusinessTypeSettingState();
  }, [modalClosed]);

  const setIsRefreshPage = () => {
    setPageInfo({ ...pageInfo, isRefreshPage: false });
  };

  const handleVisibility = () => {
    setIsDetailsVisible((e) => !e);
  };
  const [selectedId, setSelectedId] = useState<number>(-1);
  const [editableId, setEditableId] = useState<number>(-1);
  const [disablePagination, setDisablePagination] = useState<boolean>(false);
  const [pageLimit, setPageLimit] = useState<number>(initialPageLimit);
  const [pageInfo, setPageInfo] = useState<IPageInfo>({
    currentPage: 1,
    totalPages: 1,
    isPagerChange: false,
    isRefreshPage: false,
  });
  const onPageChanged = useCallback((pageNumber: number) => {
    setPageInfo((pageInfo) => ({ ...pageInfo, currentPage: pageNumber }));
  }, []);
  const onPageLimitChanged = (evt: React.ChangeEvent<HTMLSelectElement>) => {
    let value = parseInt(evt.target.value);
    setPageLimit(value);
  };
  const [sorting, setSorting] = useState<ISorting>({
    column: "userName",
    orderBy: "asc",
  });
  const [isApiCalled, setIsApiCalled] = useState<boolean>(false);

  const callGetBusinessTypeList = useCallback(() => {
    setDisablePagination(true);
    GetAllBusinessTypeListService();
    setIsApiCalled(true);
  }, [GetAllBusinessTypeListService]);

  // useEffect(() => {
  //   return () => {
  //     resetFilterHandler();
  //   };
  // }, []);

  useEffect(() => {
    setPageInfo((pageInfo) => ({
      ...pageInfo,
      // totalPages: customerRateList?.TotalPages,
      totalPages: 1,
    }));
    setDisablePagination(false);
  }, [getAllBusinessTypeList]);
  // }, [getAllBusinessTypeList, customerRateList.TotalPages]);

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const onSubmitForm = (values: any) => {
    let finalValues = {
      ...values,
    };
    if (!(values.Id && values.Id > 0)) AddBusinessTypeService(finalValues);
    else UpdateBusinessTypeService(values.Id, finalValues);
  };

  const deleteHandler = () => {
    DeleteBusinessTypeService(selectedId);
    deletePopupToggleHandler();
    setEditableId(-1);
    resetAddUpdateBusinessTypeSettingState();
  };

  const editHandler = (row: { Id: number }) => {
    setEditableId(row.Id);
    GetBusinessTypeByIdService(row.Id);
  };

  useEffect(() => {
    callGetBusinessTypeList();
  }, [callGetBusinessTypeList]);

  useEffect(() => {
    if (addUpdateBusinessTypeSettingState.success) {
      if (editableId > 0) {
        toast.success(formatMessage("business_type_setting_update_success"));
      } else {
        toast.success(formatMessage("business_type_setting_add_success"));
      }
      callGetBusinessTypeList();
      resetAddUpdateBusinessTypeSettingState();
      setEditableId(-1);
    }
  }, [
    addUpdateBusinessTypeSettingState.success,
    callGetBusinessTypeList,
    resetAddUpdateBusinessTypeSettingState,
    editableId,
  ]);
  

  useEffect(() => {
    if (deleteBusinessTypeSettingState.success) {
      resetdDeleteBusinessTypeSettingState();
      toast.success(formatMessage("business_type_setting_delete_success"));
      callGetBusinessTypeList();
    }
  }, [
    deleteBusinessTypeSettingState.success,
    callGetBusinessTypeList,
    resetdDeleteBusinessTypeSettingState,
  ]);

  return (
    <React.Fragment>
      {showDeletePopup && (
        <DeletePopup
          title="delete_business_type"
          modalSubmit={deleteHandler}
          modalClosed={deletePopupToggleHandler}
          isLoading={deleteBusinessTypeSettingState.inProgress}
          message="delete_business_type_confirm"
        />
      )}
    
      
        <AddBusinessTypeSettingsForm
          submitHandler={onSubmitForm}
          addUpdateInProgress={addUpdateBusinessTypeSettingState.inProgress}
          actionSuccess={addUpdateBusinessTypeSettingState.success}
          initialValues={getBusinessTypeDetail}
          id={editableId}
          onCancelEdit={() => {
            setEditableId(-1);
            resetAddUpdateBusinessTypeSettingState();
          }}
        />
        <BusinessTypeListView
          setVisiblity={handleVisibility}
          id={id}
          showDeletePopup={showDeletePopup}
          deleteToggleHandler={deletePopupToggleHandler}
          onPageLimitChanged={onPageLimitChanged}
          pageLimit={pageLimit}
          pageInfo={pageInfo}
          onPageChanged={onPageChanged}
          disablePagination={disablePagination}
          sorting={sorting}
          handleSortingChange={handleSortingChange}
          setSelectedId={setSelectedId}
          selectedId={selectedId}
          editHandler={editHandler}
          setIsRefreshPage={setIsRefreshPage}
        />
 
    </React.Fragment>
  );
};

export default DeletePopupHOC(observer(BusinessTypeSettings));
